import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { adminAnalyticsService } from "../../services/adminAnalyticsService";
import {
  CTA_EVENT_COLUMNS,
  EVENT_TYPE_OPTIONS,
  PAGE_TYPE_OPTIONS,
  csvEscape,
  formatAnalyticsDate,
  formatAnalyticsNumber,
} from "../../utils/analyticsFormatters";
import Toast from "../Toast/Toast";
import "./CurrentAffairsQuizAdmin.css";

const INITIAL_FILTERS = {
  dateFrom: "",
  dateTo: "",
  eventType: "",
  pageType: "",
  searchTerm: "",
};

const INITIAL_SORT = {
  key: "totalCtaClicks",
  direction: "desc",
};

const sortRows = (rows, sortConfig) => {
  const { key, direction } = sortConfig;
  const multiplier = direction === "asc" ? 1 : -1;

  return [...rows].sort((left, right) => {
    const leftValue = left?.[key];
    const rightValue = right?.[key];

    if (typeof leftValue === "number" || typeof rightValue === "number") {
      return (Number(leftValue || 0) - Number(rightValue || 0)) * multiplier;
    }

    return String(leftValue || "").localeCompare(String(rightValue || "")) * multiplier;
  });
};

function SummaryCard({ label, value, helperText }) {
  return (
    <div className="ca-admin-stat-card">
      <span>{label}</span>
      <strong>{formatAnalyticsNumber(value)}</strong>
      {helperText ? <small className="ca-admin-summary-helper">{helperText}</small> : null}
    </div>
  );
}

function TopPagesTable({ title, rows, clickLabel }) {
  return (
    <section className="ca-admin-list-card">
      <div className="ca-admin-header">
        <h2>{title}</h2>
      </div>

      {!rows.length ? (
        <p className="ca-admin-empty-state">No matching data for this event right now.</p>
      ) : (
        <div className="ca-admin-table-wrap">
          <table className="ca-admin-table">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Item Title</th>
                <th>Page Path</th>
                <th>{clickLabel}</th>
                <th>Page Type</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={`${row.pagePath}-${row.rank}`}>
                  <td data-label="Rank">{row.rank}</td>
                  <td data-label="Item Title">{row.itemTitle || "NA"}</td>
                  <td data-label="Page Path">{row.pagePath || "NA"}</td>
                  <td data-label={clickLabel}>{formatAnalyticsNumber(row.totalClicks)}</td>
                  <td data-label="Page Type">{row.pageType || "NA"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function CombinedPerformanceTable({
  rows,
  sortConfig,
  onSort,
  currentPage,
  rowsPerPage,
}) {
  const pageStart = (currentPage - 1) * rowsPerPage;
  const pagedRows = rows.slice(pageStart, pageStart + rowsPerPage);

  const renderSortableHead = (columnKey, label) => (
    <th>
      <button
        type="button"
        className="ca-admin-table-sort"
        onClick={() => onSort(columnKey)}
      >
        {label}
        {sortConfig.key === columnKey ? (
          <span>{sortConfig.direction === "asc" ? " ↑" : " ↓"}</span>
        ) : null}
      </button>
    </th>
  );

  return (
    <section className="ca-admin-list-card" style={{ marginTop: 16 }}>
      <div className="ca-admin-header">
        <h2>Combined CTA Performance</h2>
      </div>

      {!rows.length ? (
        <p className="ca-admin-empty-state">No rows match the current filters.</p>
      ) : (
        <div className="ca-admin-table-wrap">
          <table className="ca-admin-table">
            <thead>
              <tr>
                {renderSortableHead("itemTitle", "Item Title")}
                {renderSortableHead("pagePath", "Page Path")}
                {renderSortableHead("pageType", "Page Type")}
                {CTA_EVENT_COLUMNS.map((column) => renderSortableHead(column.key, column.label))}
                {renderSortableHead("totalCtaClicks", "Total CTA Clicks")}
              </tr>
            </thead>
            <tbody>
              {pagedRows.map((row) => (
                <tr key={row.pagePath}>
                  <td data-label="Item Title">
                    <strong>{row.itemTitle || "NA"}</strong>
                    <div className="ca-admin-subtitle">
                      Last event: {formatAnalyticsDate(row.lastEventDate)}
                    </div>
                  </td>
                  <td data-label="Page Path">
                    <Link className="ca-admin-inline-link" to={row.pagePath}>
                      {row.pagePath}
                    </Link>
                  </td>
                  <td data-label="Page Type">{row.pageType || "NA"}</td>
                  <td data-label="Apply Clicks">{formatAnalyticsNumber(row.applyClicks)}</td>
                  <td data-label="Result Clicks">{formatAnalyticsNumber(row.resultClicks)}</td>
                  <td data-label="Admit Card Clicks">{formatAnalyticsNumber(row.admitCardClicks)}</td>
                  <td data-label="Notification Downloads">
                    {formatAnalyticsNumber(row.notificationDownloads)}
                  </td>
                  <td data-label="Official Website Clicks">
                    {formatAnalyticsNumber(row.officialWebsiteClicks)}
                  </td>
                  <td data-label="Answer Key Clicks">
                    {formatAnalyticsNumber(row.answerKeyClicks)}
                  </td>
                  <td data-label="Syllabus Clicks">
                    {formatAnalyticsNumber(row.syllabusClicks)}
                  </td>
                  <td data-label="Total CTA Clicks">
                    <strong>{formatAnalyticsNumber(row.totalCtaClicks)}</strong>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function AdminAnalyticsDashboardPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toastMessage, setToastMessage] = useState("");
  const [sortConfig, setSortConfig] = useState(INITIAL_SORT);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    let mounted = true;

    const loadDashboard = async () => {
      setLoading(true);
      setError("");
      try {
        const data = await adminAnalyticsService.getDashboard(filters);
        if (!mounted) return;
        setDashboardData(data);
      } catch (err) {
        if (!mounted) return;
        const nextError = err?.message || "Unable to load analytics dashboard.";
        setError(nextError);
        setToastMessage(nextError);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadDashboard();
    return () => {
      mounted = false;
    };
  }, [filters]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters, sortConfig]);

  const combinedRows = useMemo(() => {
    const rows = dashboardData?.combinedPerformance || [];
    return sortRows(rows, sortConfig);
  }, [dashboardData, sortConfig]);

  const rowsPerPage = 8;
  const totalPages = Math.max(1, Math.ceil(combinedRows.length / rowsPerPage));
  const summary = dashboardData?.summary || {};

  const handleFilterChange = (field, value) => {
    setFilters((current) => ({ ...current, [field]: value }));
  };

  const handleSort = (key) => {
    setSortConfig((current) => {
      if (current.key === key) {
        return { key, direction: current.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "desc" };
    });
  };

  const handleExportCsv = () => {
    if (!combinedRows.length) return;

    const headers = [
      "Item Title",
      "Page Path",
      "Page Type",
      ...CTA_EVENT_COLUMNS.map((column) => column.label),
      "Total CTA Clicks",
      "Last Event Date",
    ];

    const lines = [
      headers.map(csvEscape).join(","),
      ...combinedRows.map((row) =>
        [
          row.itemTitle,
          row.pagePath,
          row.pageType,
          row.applyClicks,
          row.resultClicks,
          row.admitCardClicks,
          row.notificationDownloads,
          row.officialWebsiteClicks,
          row.answerKeyClicks,
          row.syllabusClicks,
          row.totalCtaClicks,
          row.lastEventDate,
        ]
          .map(csvEscape)
          .join(",")
      ),
    ];

    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = "aamschool-admin-analytics.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(objectUrl);
  };

  return (
    <div className="ca-admin-page">
      <div className="ca-admin-header ca-admin-header-modern">
        <div>
          <p className="ca-admin-hero-kicker">Admin Analytics</p>
          <h1>CTA Performance Dashboard</h1>
          <p className="ca-admin-subtitle">
            Review top-performing pages and CTA engagement using an integration-ready analytics UI.
          </p>
        </div>
        <div className="ca-admin-actions">
          <button type="button" className="ca-admin-btn" onClick={() => navigate("/admin/dashboard/quiz")}>
            Back To Dashboard
          </button>
          <button type="button" className="ca-admin-btn" onClick={handleExportCsv} disabled={!combinedRows.length}>
            Export CSV
          </button>
        </div>
      </div>

      {error ? <p className="ca-admin-msg err">{error}</p> : null}

      <section className="ca-admin-list-card ca-admin-form-card">
        <div className="ca-admin-header">
          <h2>Filters</h2>
          <div className="ca-admin-current-filter compact">
            <span>Source</span>
            <strong>{dashboardData?.meta?.source || "mock"}</strong>
          </div>
        </div>

        <div className="ca-admin-analytics-filter-grid">
          <div>
            <label htmlFor="analytics-date-from">Date From</label>
            <input
              id="analytics-date-from"
              type="date"
              value={filters.dateFrom}
              onChange={(event) => handleFilterChange("dateFrom", event.target.value)}
            />
          </div>
          <div>
            <label htmlFor="analytics-date-to">Date To</label>
            <input
              id="analytics-date-to"
              type="date"
              value={filters.dateTo}
              onChange={(event) => handleFilterChange("dateTo", event.target.value)}
            />
          </div>
          <div>
            <label htmlFor="analytics-event-type">Event Type</label>
            <select
              id="analytics-event-type"
              value={filters.eventType}
              onChange={(event) => handleFilterChange("eventType", event.target.value)}
            >
              {EVENT_TYPE_OPTIONS.map((option) => (
                <option key={option.value || "all"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="analytics-page-type">Page Type</label>
            <select
              id="analytics-page-type"
              value={filters.pageType}
              onChange={(event) => handleFilterChange("pageType", event.target.value)}
            >
              {PAGE_TYPE_OPTIONS.map((option) => (
                <option key={option.value || "all"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div className="ca-admin-analytics-search">
            <label htmlFor="analytics-search-term">Search Title / Path</label>
            <input
              id="analytics-search-term"
              value={filters.searchTerm}
              placeholder="Search item title or page path"
              onChange={(event) => handleFilterChange("searchTerm", event.target.value)}
            />
          </div>
        </div>
      </section>

      {loading ? <p>Loading analytics dashboard...</p> : null}

      {!loading ? (
        <>
          <section className="ca-admin-stats-grid ca-admin-analytics-summary-grid">
            <SummaryCard label="Total CTA Clicks" value={summary.totalCtaClicks} />
            <SummaryCard label="Total Apply Clicks" value={summary.totalApplyClicks} />
            <SummaryCard label="Total Result Clicks" value={summary.totalResultClicks} />
            <SummaryCard label="Total Admit Card Clicks" value={summary.totalAdmitCardClicks} />
            <SummaryCard
              label="Notification Downloads"
              value={summary.totalNotificationDownloads}
            />
            <SummaryCard
              label="Top Performing Page"
              value={summary.topPerformingPage ? 1 : 0}
              helperText={summary.topPerformingPage || "NA"}
            />
            <SummaryCard
              label="Top Page Type"
              value={summary.topPerformingPageType ? 1 : 0}
              helperText={summary.topPerformingPageType || "NA"}
            />
          </section>

          <div className="ca-admin-analytics-table-grid">
            <TopPagesTable
              title="Top Apply Click Pages"
              rows={dashboardData?.topApplyPages || []}
              clickLabel="Apply Clicks"
            />
            <TopPagesTable
              title="Top Result Click Pages"
              rows={dashboardData?.topResultPages || []}
              clickLabel="Result Clicks"
            />
            <TopPagesTable
              title="Top Admit Card Click Pages"
              rows={dashboardData?.topAdmitCardPages || []}
              clickLabel="Admit Card Clicks"
            />
            <TopPagesTable
              title="Top Notification Download Pages"
              rows={dashboardData?.topNotificationPages || []}
              clickLabel="Notification Downloads"
            />
          </div>

          <CombinedPerformanceTable
            rows={combinedRows}
            sortConfig={sortConfig}
            onSort={handleSort}
            currentPage={currentPage}
            rowsPerPage={rowsPerPage}
          />

          {combinedRows.length ? (
            <div className="ca-admin-pagination">
              <button
                type="button"
                className="ca-admin-btn"
                disabled={currentPage <= 1}
                onClick={() => setCurrentPage((current) => Math.max(1, current - 1))}
              >
                Previous
              </button>
              <span>
                Page {currentPage} of {totalPages}
              </span>
              <button
                type="button"
                className="ca-admin-btn"
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage((current) => Math.min(totalPages, current + 1))}
              >
                Next
              </button>
            </div>
          ) : null}
        </>
      ) : null}

      <Toast message={toastMessage} onClose={() => setToastMessage("")} />
    </div>
  );
}

export default AdminAnalyticsDashboardPage;
